/**
 * Paths of the Telegram Mini App (/[locale]/tg/...) and the t.me start
 * parameters that lead to them. Mirrors the backend's
 * services/telegram_mini_app: ``l_<listing slug>``, ``r_<residence id>``,
 * ``d_<developer slug>``, ``sl_<shared list id>``, ``a_<alert id>``,
 * ``saved``, ``alerts``. Start parameters may only hold A-Z a-z 0-9 _ -
 * (Bot API), which every one of them satisfies. None of them signs anyone
 * in: a screen loads what it shows with the signed launch data (or the
 * session minted from it), whatever the link says.
 */
import { isListingSlug, listingIdFromSlug, listingSlug } from "@/lib/seo/paths";

export const MINI_APP_HOME = "/tg";
export const MINI_APP_ALERTS = "/tg/alerts";

/** A saved search's id (backend saved_searches.id). */
const ALERT_ID_RE = /^[1-9][0-9]{0,9}$/;

/** A developer's slug (backend data/developers.json). */
const DEVELOPER_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,79}$/;

/** A developer in the app: its listings on the browse screen (the app has
 *  no developer page; the residence screen's "all listings" does the same
 *  with ``complex_slug``). Null for anything but a slug. */
export function miniAppDeveloperPath(slug: string): string | null {
  return DEVELOPER_SLUG_RE.test(slug) ? `${MINI_APP_HOME}?developer=${slug}` : null;
}

/** @public (the unit tests exercise it directly) */
export function developerStartParam(slug: string): string | null {
  return DEVELOPER_SLUG_RE.test(slug) ? `d_${slug}` : null;
}

/**
 * Whether the launch URL's language is the one to keep. The bot's own
 * buttons open a screen (``/ru/tg/r/<id>``, ``/ru/tg?developer=...``) in
 * the language the person uses in the bot; the menu button and the
 * Main Mini App always open the bare home in Azerbaijani, where Telegram's
 * own language is the better guess. Telegram's launch keys
 * (``tgWebApp...``) and the dev stand-in's (``tg_...``) do not count.
 */
export function keepsLaunchLocale(pathname: string, search: string): boolean {
  if (!isMiniAppPath(pathname)) return false;
  if (!isMiniAppHome(pathname)) return true;
  const params = new URLSearchParams(search);
  return Array.from(params.keys()).some((k) => !/^tg/i.test(k));
}

/** One alert's matches. */
export function miniAppAlertPath(id: number | string): string {
  return `${MINI_APP_ALERTS}/${id}`;
}

/** The screen that edits one alert's filters (the browse screen). */
export function miniAppAlertEditPath(id: number | string): string {
  return `${MINI_APP_ALERTS}/${id}/edit`;
}

/** @public (the unit tests exercise it directly) */
export function alertStartParam(id: number | string): string | null {
  return ALERT_ID_RE.test(String(id)) ? `a_${id}` : null;
}

/** An analysis run from the app: its history id, or "last" for a run the
 *  app only holds in memory (lib/mini-app-analysis-store). */
export function miniAppAnalysisPath(key: string | number): string {
  return `/tg/a/${key}`;
}

const MINI_APP_RE = /^\/(?:az|ru|en)\/tg(?:\/|$)/;
const RESIDENCE_ID_RE = /^[a-z0-9][a-z0-9-]{0,119}$/;

/** True on any Mini App page (the site chrome stays off there). */
export function isMiniAppPath(pathname: string | null | undefined): boolean {
  return !!pathname && MINI_APP_RE.test(pathname);
}

/** The app's home without the locale ("/tg") for a locale-prefixed path. */
export function isMiniAppHome(pathname: string | null | undefined): boolean {
  return !!pathname && /^\/(?:az|ru|en)\/tg\/?$/.test(pathname);
}

export function listingStartParam(listingId: string): string | null {
  const slug = listingSlug(listingId);
  return slug ? `l_${slug}` : null;
}

export function residenceStartParam(clusterId: string): string | null {
  return RESIDENCE_ID_RE.test(clusterId) ? `r_${clusterId}` : null;
}

/** A shared list's id (backend services/telegram_shortlists.new_id). */
const SHORTLIST_ID_RE = /^[A-Za-z0-9_-]{8,16}$/;

export function shortlistStartParam(id: string): string | null {
  return SHORTLIST_ID_RE.test(id) ? `sl_${id}` : null;
}

/** The screen a start parameter opens (locale-less), or null for the home. */
export function pathForStartParam(param: string | null | undefined): string | null {
  if (!param || !/^[A-Za-z0-9_-]{1,512}$/.test(param)) return null;
  if (param === "saved") return `${MINI_APP_HOME}/saved`;
  if (param === "alerts") return MINI_APP_ALERTS;
  const sep = param.indexOf("_");
  if (sep < 0) return null;
  const kind = param.slice(0, sep);
  const rest = param.slice(sep + 1);
  if (kind === "l" && isListingSlug(rest)) return `${MINI_APP_HOME}/l/${rest}`;
  if (kind === "r" && RESIDENCE_ID_RE.test(rest)) return `${MINI_APP_HOME}/r/${rest}`;
  if (kind === "sl" && SHORTLIST_ID_RE.test(rest)) return `${MINI_APP_HOME}/s/${rest}`;
  if (kind === "a" && ALERT_ID_RE.test(rest)) return miniAppAlertPath(rest);
  if (kind === "d") return miniAppDeveloperPath(rest);
  return null;
}

/** The index id ("bina_az:123") of a Mini App listing slug. */
export function listingIdOfSlug(slug: string): string | null {
  return isListingSlug(slug) ? listingIdFromSlug(slug) : null;
}

/** Where a link inside a site component goes from the app: a listing or
 *  residence page opens the app's own screen, an app path stays, and
 *  anything else (another site page, another host) opens outside the
 *  app through Telegram; an anchor or a non-web scheme is left alone. */
export type MiniAppLinkTarget =
  | { kind: "app"; path: string }
  | { kind: "external"; url: string }
  | { kind: "none" };

export function miniAppLinkTarget(href: string, origin: string): MiniAppLinkTarget {
  if (!href || href.startsWith("#")) return { kind: "none" };
  let u: URL;
  try {
    u = new URL(href, origin);
  } catch {
    return { kind: "none" };
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return { kind: "none" };
  if (u.origin !== origin) return { kind: "external", url: u.toString() };
  const m = /^\/(az|ru|en)\/(elan|residence)\/([^/?#]+)\/?$/.exec(u.pathname);
  if (m) return { kind: "app", path: `/${m[1]}/tg/${m[2] === "elan" ? "l" : "r"}/${m[3]}` };
  if (MINI_APP_RE.test(u.pathname)) return { kind: "app", path: `${u.pathname}${u.search}` };
  return { kind: "external", url: u.toString() };
}
